import React, { useState, useEffect, useMemo } from "react";
import Header from "../../../layout/Header";
import { backIcon2 } from "../../../../Image/Index";
import { Link, useNavigate } from "react-router-dom";
import { getCustomerPortalSetting, socialProfileSetting } from "../../campaigns/services";
import swal from "sweetalert";
import { decrypt } from "../../../../utils/encodingdecoding";

const moduleMeta = {
    "Goal & Milestone Tracking": { icon: "🎯", color: "#3b82f6", bg: "#eaf1ff", desc: "Milestone progress and achievement rewards" },
    "Scratch Card Rewards": { icon: "🎟️", color: "#8b5cf6", bg: "#f2edff", desc: "Scratch cards and instant win rewards" },
    "Games & Challenges": { icon: "🎮", color: "#8b5cf6", bg: "#f2edff", desc: "Fun games and win bonus points" },
    "Receipt Upload Verification": { icon: "🧾", color: "#0ea5e9", bg: "#e8f7ff", desc: "Upload and verify bills for points" },
    "QR Code Scanning": { icon: "📷", color: "#22c55e", bg: "#e9fbef", desc: "Scan QR codes to earn points" },
    "Cashback Rewards": { icon: "💵", color: "#22c55e", bg: "#e9fbef", desc: "Ways to earn cashback and points" },
    "Gift Card Redemption": { icon: "🎁", color: "#ef4444", bg: "#feecec", desc: "Redeem points for gift cards" },
    "Voucher Redemption": { icon: "🏷️", color: "#f59e0b", bg: "#fff6e5", desc: "Redeem points for vouchers" },
    "Product Redemption": { icon: "📦", color: "#b8895a", bg: "#f7efe6", desc: "Redeem points for exciting products" },
    "Premium Concierge": { icon: "🎩", color: "#d946ef", bg: "#fbe9ff", desc: "Priority support and exclusive concierge service" },
    "Member Performance Rankings": { icon: "🏆", color: "#f59e0b", bg: "#fff6e5", desc: "Leaderboard showing top performing members" },
};

// Which modules show under "Earn Points" vs "Redeem Points" in the preview
const earnLabels = ["Scratch Card Rewards", "Games & Challenges", "Receipt Upload Verification", "QR Code Scanning"];
const redeemLabels = ["Cashback Rewards", "Gift Card Redemption", "Voucher Redemption", "Product Redemption", "Premium Concierge"];

const earnPreviewMeta = {
    "Scratch Card Rewards": { icon: "🎫", bg: "#eaf1ff", label: "Scratch Card" },
    "Games & Challenges": { icon: "🎮", bg: "#f2edff", label: "Games" },
    "Receipt Upload Verification": { icon: "🧾", bg: "#e8f7ff", label: "Receipt Upload" },
    "QR Code Scanning": { icon: "📷", bg: "#e9fbef", label: "QR Scan" },
};
const redeemPreviewMeta = {
    "Cashback Rewards": { icon: "💵", bg: "#e9fbef", label: "Cashback" },
    "Gift Card Redemption": { icon: "🎁", bg: "#feecec", label: "Gift Card" },
    "Voucher Redemption": { icon: "🏷️", bg: "#fff6e5", label: "Voucher" },
    "Product Redemption": { icon: "📦", bg: "#f7efe6", label: "Product" },
    "Premium Concierge": { icon: "🎩", bg: "#fbe9ff", label: "Concierge" },
};

// Dummy leaderboard data for the preview panel
const leaderboardPreviewData = [
    { rank: 1, name: "Aarav Sharma", points: "24,500", isYou: false },
    { rank: 2, name: "You", points: "18,200", isYou: true },
    { rank: 3, name: "Priya Verma", points: "15,900", isYou: false },
];

// Default full module list — always used as the source of truth for
// which options should be visible. API only supplies the "enabled" flag.
const defaultSettings = [
    { label: "Goal & Milestone Tracking", enabled: false },
    { label: "Scratch Card Rewards", enabled: false },
    { label: "Games & Challenges", enabled: false },
    { label: "Receipt Upload Verification", enabled: false },
    { label: "QR Code Scanning", enabled: false },
    { label: "Cashback Rewards", enabled: false },
    { label: "Gift Card Redemption", enabled: false },
    { label: "Voucher Redemption", enabled: false },
    { label: "Product Redemption", enabled: false },
    { label: "Premium Concierge", enabled: false },
    { label: "Member Performance Rankings", enabled: false },
];

const PayByPoint = () => {
    let navigate = useNavigate();
    const [loader, setLoader] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [settings, setSettings] = useState(defaultSettings);

    useEffect(() => {
        fetch()
    }, [])

    const fetch = async () => {
        try {
            setLoader(true)
            const token = JSON.parse(localStorage.getItem('token_gen'));
            const res = await getCustomerPortalSetting(token?.access_token);
            if (res.message === 'Settings retrieved successfully') {
                const apiSettings = res.data?.others?.portalSetting;

                // Always merge with the full default list so that no
                // option ever disappears, even if the API returns an
                // empty array, a partial list, or undefined.
                const merged = defaultSettings.map((def) => {
                    const found = apiSettings?.find((s) => s.label === def.label);
                    return found ? { ...def, enabled: !!found.enabled } : def;
                });

                setSettings(merged);
            }
            // if message doesn't match, settings stays on the default full list
        } catch (err) {
            console.log(err)
        } finally {
            setLoader(false)
        }
    }

    const handleToggle = (label) => {
        setSettings((prev) =>
            prev.map((item) =>
                item.label === label
                    ? { ...item, enabled: !item.enabled }
                    : item
            )
        );
        setDirty(true);
    };

    const handleSave = async () => {
        let res
        try {
            let token = JSON.parse(localStorage.getItem("token_gen"));
            setLoader(true)
            const body = {
                others: {
                    portalSetting: settings
                }
            }
            res = await socialProfileSetting(token?.access_token, body)
            if (res.message === "Settings Updated Successfully") {
                swal({
                    position: "center",
                    icon: "success",
                    title: "Portal Configuration Updated Successfully",
                    showConfirmButton: false,
                    timer: 2000,
                });
                setDirty(false);
            }
        } catch (err) {
            swal({
                position: "center",
                icon: "error",
                title: res.message,
                showConfirmButton: false,
                timer: 2000,
            });
        } finally {
            setLoader(false)
        }

    };

    const enabledMap = useMemo(() => {
        const map = {};
        settings?.forEach((s) => { map[s.label] = s.enabled; });
        return map;
    }, [settings]);

    const visibleEarn = earnLabels?.filter((l) => enabledMap[l]);
    const visibleRedeem = redeemLabels?.filter((l) => enabledMap[l]);
    const goalEnabled = enabledMap["Goal & Milestone Tracking"];
    const leaderboardEnabled = enabledMap["Member Performance Rankings"];

    return (
        <>
            <style>{`
                .ppPageWrap {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                }

                .ppMainGrid {
                    display: grid;
                    grid-template-columns: 1fr 340px;
                    gap: 20px;
                    align-items: start;
                }
                @media (max-width: 1100px) {
                    .ppMainGrid {
                        grid-template-columns: 1fr;
                    }
                }

                .ppSettingsCard {
                    background: #ffffff;
                    border-radius: 16px;
                    padding: 0;
                    box-shadow: 0 4px 24px rgba(15, 23, 42, 0.06);
                    overflow: hidden;
                    border: 1px solid #eef0f4;
                }
                .ppSettingsCardHeader {
                    padding: 20px 24px;
                    border-bottom: 1px solid #eef0f4;
                }
                .ppSettingsCardHeader h4 {
                    margin: 0;
                    font-size: 16.5px;
                    font-weight: 600;
                    color: #1f2937;
                }
                .ppSettingsCardHeader p {
                    margin: 4px 0 0;
                    font-size: 13px;
                    color: #8a8f9c;
                }

                .ppModuleList {
                    display: flex;
                    flex-direction: column;
                }
                .ppModuleRow {
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    padding: 16px 24px;
                    border-bottom: 1px solid #f3f4f7;
                    transition: background 0.15s ease;
                }
                .ppModuleRow:last-child {
                    border-bottom: none;
                }
                .ppModuleRow:hover {
                    background: #fafbfd;
                }
                .ppDragHandle {
                    color: #cbd0da;
                    font-size: 14px;
                    cursor: grab;
                    flex-shrink: 0;
                }
                .ppModuleIcon {
                    width: 40px;
                    height: 40px;
                    border-radius: 11px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 18px;
                    flex-shrink: 0;
                }
                .ppModuleText {
                    flex: 1;
                    min-width: 0;
                }
                .ppModuleText strong {
                    display: block;
                    font-size: 14.5px;
                    color: #1f2937;
                    font-weight: 600;
                }
                .ppModuleText span {
                    display: block;
                    font-size: 12.5px;
                    color: #9aa1ae;
                    margin-top: 2px;
                }
                .ppModuleToggle {
                    width: 42px;
                    height: 22px;
                    cursor: pointer;
                    background-color: #d7dbe3;
                    border-color: #d7dbe3;
                    flex-shrink: 0;
                    transition: background-color 0.25s ease, border-color 0.25s ease;
                }
                .ppModuleToggle:checked {
                    background-color: #6c5ce7;
                    border-color: #6c5ce7;
                }
                .ppModuleToggle:focus {
                    box-shadow: none;
                }

                .ppSettingsFooter {
                    display: flex;
                    justify-content: flex-end;
                    align-items: center;
                    gap: 14px;
                    padding: 16px 24px;
                    background: #fafbfd;
                    border-top: 1px solid #eef0f4;
                }
                .ppDirtyBadge {
                    font-size: 12.5px;
                    color: #d97706;
                    background: #fff7ea;
                    padding: 5px 12px;
                    border-radius: 20px;
                    font-weight: 500;
                    opacity: ${dirty ? 1 : 0};
                    transition: opacity 0.2s ease;
                }
                .ppSaveBtn {
                    padding: 10px 30px;
                    border: none;
                    border-radius: 8px;
                    background: #6c5ce7;
                    color: #fff;
                    font-weight: 500;
                    font-size: 15px;
                    box-shadow: 0 4px 12px rgba(108, 92, 231, 0.28);
                    transition: background 0.2s ease, transform 0.15s ease, box-shadow 0.2s ease;
                }
                .ppSaveBtn:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 6px 16px rgba(108, 92, 231, 0.35);
                }
                .ppSaveBtn:active {
                    transform: translateY(0);
                }

                /* ===== Preview Panel (mirrors actual portal UI) ===== */
                .ppPreviewCard {
                    background: #f7f8fb;
                    border-radius: 16px;
                    box-shadow: 0 4px 24px rgba(15, 23, 42, 0.06);
                    border: 1px solid #eef0f4;
                    overflow: hidden;
                    position: sticky;
                    top: 16px;
                }
                .ppPreviewHeader {
                    padding: 14px 18px;
                    border-bottom: 1px solid #eef0f4;
                    text-align: center;
                }
                .ppPreviewHeader h5 {
                    margin: 0;
                    font-size: 13.5px;
                    font-weight: 600;
                    color: #9aa1ae;
                    letter-spacing: 0.4px;
                    text-transform: uppercase;
                }

                .ppPortalScroll {
                    max-height: 640px;
                    overflow-y: auto;
                    padding: 14px 14px 20px;
                }
                .ppPortalScroll::-webkit-scrollbar {
                    width: 5px;
                }
                .ppPortalScroll::-webkit-scrollbar-thumb {
                    background: #e0e2ea;
                    border-radius: 10px;
                }

                .ppPortalTopBar {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 14px;
                }
                .ppPortalLogo {
                    width: 34px;
                    height: 34px;
                    border-radius: 9px;
                    background: #6c5ce7;
                    color: #fff;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 12px;
                    font-weight: 700;
                }
                .ppPortalUser {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                .ppPortalUser span {
                    font-size: 12.5px;
                    font-weight: 500;
                    color: #374151;
                }
                .ppPortalIconCircle {
                    width: 26px;
                    height: 26px;
                    border-radius: 50%;
                    background: #f0edff;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 12px;
                }
                .ppPortalAvatarCircle {
                    width: 26px;
                    height: 26px;
                    border-radius: 50%;
                    background: #6c5ce7;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 11px;
                    color: #fff;
                }

                .ppLoyaltyBanner {
                    background: linear-gradient(135deg, #6c3ce9, #4b2ec9);
                    border-radius: 16px;
                    padding: 16px 16px 20px;
                    color: #fff;
                    position: relative;
                    overflow: hidden;
                    margin-bottom: 14px;
                }
                .ppLoyaltyTag {
                    display: inline-block;
                    background: rgba(255,255,255,0.18);
                    font-size: 9.5px;
                    font-weight: 700;
                    letter-spacing: 0.6px;
                    padding: 3px 9px;
                    border-radius: 20px;
                    margin-bottom: 8px;
                }
                .ppLoyaltyBanner h3 {
                    margin: 0;
                    font-size: 17px;
                    font-weight: 700;
                    line-height: 1.3;
                }
                .ppLoyaltyBanner h3.ppOrange {
                    color: #fbbf24;
                }
                .ppLoyaltyBanner p {
                    margin: 8px 0 0;
                    font-size: 11.5px;
                    opacity: 0.85;
                }
                .ppLoyaltyIconWrap {
                    position: absolute;
                    right: 14px;
                    top: 14px;
                    width: 44px;
                    height: 44px;
                    border-radius: 50%;
                    background: rgba(255,255,255,0.15);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 20px;
                }
                .ppLoyaltyDots {
                    display: flex;
                    justify-content: center;
                    gap: 5px;
                    margin: 10px 0 14px;
                }
                .ppLoyaltyDots span {
                    width: 5px;
                    height: 5px;
                    border-radius: 50%;
                    background: #d7d2ee;
                }
                .ppLoyaltyDots span:first-child {
                    background: #6c3ce9;
                    width: 14px;
                    border-radius: 4px;
                }

                .ppPtsStatsRow {
                    display: flex;
                    align-items: center;
                    background: #fff;
                    border-radius: 14px;
                    padding: 6px;
                    margin-bottom: 16px;
                    box-shadow: 0 2px 10px rgba(15,23,42,0.04);
                }
                .ppPtsStatBox {
                    flex: 1;
                    text-align: center;
                    padding: 12px 4px;
                }
                .ppPtsStatBox.ppActivePts {
                    background: linear-gradient(135deg, #6c5ce7, #5b4bd1);
                    border-radius: 10px;
                    color: #fff;
                }
                .ppPtsStatBox strong {
                    display: block;
                    font-size: 15px;
                    font-weight: 700;
                    color: #1f2937;
                }
                .ppPtsStatBox.ppActivePts strong {
                    color: #fff;
                }
                .ppPtsStatBox small {
                    font-size: 10.5px;
                    color: #9aa1ae;
                }
                .ppPtsStatBox.ppActivePts small {
                    color: rgba(255,255,255,0.85);
                }

                .ppSectionHeadRow {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 8px;
                }
                .ppSectionHeadRow h6 {
                    margin: 0;
                    font-size: 13.5px;
                    font-weight: 700;
                    color: #1f2937;
                }
                .ppSectionHeadRow a {
                    font-size: 11.5px;
                    color: #6c5ce7;
                    font-weight: 600;
                }

                .ppGoalCard {
                    background: #fff;
                    border-radius: 14px;
                    border: 1px solid #ece7ff;
                    padding: 14px 16px;
                    margin-bottom: 18px;
                    box-shadow: 0 2px 10px rgba(15,23,42,0.04);
                }
                .ppGoalTopRow {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                }
                .ppActiveGoalTag {
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                    background: #f0edff;
                    color: #6c5ce7;
                    font-size: 10.5px;
                    font-weight: 600;
                    padding: 4px 10px;
                    border-radius: 20px;
                }
                .ppGoalRing {
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    border: 3px solid #6c5ce7;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                }
                .ppGoalRing strong {
                    font-size: 10px;
                    color: #6c5ce7;
                    font-weight: 700;
                    line-height: 1;
                }
                .ppGoalRing small {
                    font-size: 7px;
                    color: #9aa1ae;
                }
                .ppGoalCard h4 {
                    margin: 10px 0 4px;
                    font-size: 14.5px;
                    font-weight: 700;
                    color: #1f2937;
                }
                .ppGoalMeta {
                    font-size: 11px;
                    color: #9aa1ae;
                    margin-bottom: 10px;
                }
                .ppGoalBar {
                    height: 6px;
                    border-radius: 20px;
                    background: #eef0f4;
                    position: relative;
                    overflow: hidden;
                }
                .ppGoalBarFill {
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(90deg, #8b5cf6, #6c5ce7);
                    border-radius: 20px;
                }

                /* ===== Leaderboard Card (Member Performance Rankings) ===== */
                .ppLeaderboardCard {
                    background: #fff;
                    border-radius: 14px;
                    border: 1px solid #ffedd5;
                    padding: 14px 16px;
                    margin-bottom: 18px;
                    box-shadow: 0 2px 10px rgba(15,23,42,0.04);
                }
                .ppLeaderboardTopRow {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 10px;
                }
                .ppLeaderboardTag {
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                    background: #fff6e5;
                    color: #d97706;
                    font-size: 10.5px;
                    font-weight: 600;
                    padding: 4px 10px;
                    border-radius: 20px;
                }
                .ppLeaderboardList {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }
                .ppLeaderboardRow {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 8px 10px;
                    border-radius: 10px;
                    background: #fafbfd;
                }
                .ppLeaderboardRow.ppIsYou {
                    background: #fff6e5;
                    border: 1px solid #ffe4ae;
                }
                .ppLeaderboardRank {
                    width: 22px;
                    height: 22px;
                    border-radius: 50%;
                    background: #f0edff;
                    color: #6c5ce7;
                    font-size: 11px;
                    font-weight: 700;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }
                .ppLeaderboardRow:nth-child(1) .ppLeaderboardRank {
                    background: #fff6e5;
                    color: #d97706;
                }
                .ppLeaderboardAvatar {
                    width: 28px;
                    height: 28px;
                    border-radius: 50%;
                    background: #6c5ce7;
                    color: #fff;
                    font-size: 11px;
                    font-weight: 700;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }
                .ppLeaderboardName {
                    flex: 1;
                    font-size: 12.5px;
                    font-weight: 600;
                    color: #1f2937;
                }
                .ppLeaderboardPts {
                    font-size: 12px;
                    font-weight: 700;
                    color: #6c5ce7;
                }

                .ppGrid2 {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 10px;
                    margin-bottom: 18px;
                }
                .ppEarnCard {
                    background: #fff;
                    border-radius: 12px;
                    padding: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 8px;
                    box-shadow: 0 2px 10px rgba(15,23,42,0.04);
                }
                .ppEarnCardLeft {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }
                .ppEarnIcon {
                    width: 32px;
                    height: 32px;
                    border-radius: 9px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 15px;
                }
                .ppEarnCard span {
                    font-size: 12.5px;
                    font-weight: 600;
                    color: #1f2937;
                }
                .ppEarnArrow {
                    width: 22px;
                    height: 22px;
                    border-radius: 50%;
                    background: #f0edff;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #6c5ce7;
                    font-size: 11px;
                    flex-shrink: 0;
                }

                .ppGrid4 {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 8px;
                    margin-bottom: 18px;
                }
                .ppRedeemCard {
                    background: #fff;
                    border-radius: 12px;
                    padding: 12px 6px;
                    text-align: center;
                    box-shadow: 0 2px 10px rgba(15,23,42,0.04);
                }
                .ppRedeemIcon {
                    width: 36px;
                    height: 36px;
                    border-radius: 50%;
                    margin: 0 auto 6px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 15px;
                }
                .ppRedeemCard span {
                    font-size: 10.5px;
                    font-weight: 500;
                    color: #4b5563;
                }

                .ppTxRow {
                    background: #fff;
                    border-radius: 12px;
                    padding: 12px 14px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 8px;
                    box-shadow: 0 2px 10px rgba(15,23,42,0.04);
                }
                .ppTxLeft {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }
                .ppTxIcon {
                    width: 30px;
                    height: 30px;
                    border-radius: 9px;
                    background: #f0edff;
                    color: #6c5ce7;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 13px;
                    font-weight: 700;
                }
                .ppTxLeft strong {
                    display: block;
                    font-size: 12.5px;
                    font-weight: 600;
                    color: #1f2937;
                }
                .ppTxLeft small {
                    font-size: 10.5px;
                    color: #9aa1ae;
                }
                .ppTxRight {
                    font-size: 13px;
                    font-weight: 700;
                    color: #16a34a;
                }

                .ppEmptyPreviewNote {
                    text-align: center;
                    font-size: 12px;
                    color: #b3b8c2;
                    padding: 10px 0;
                }
            `}</style>

            {loader && (
                <div className="mainPreloaderMain">
                    <div className="mainPreloader">
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                    </div>
                </div>
            )}
            <Header name={"Portal Setting"} />
            <div className="dashboard__content">
                <div className="container-fluid">
                    <div className="dashboard__customerViewMain ppPageWrap">

                        <div className="ppMainGrid">
                            {/* LEFT: Modules list */}
                            <div className="ppSettingsCard">
                                <div className="ppSettingsCardHeader">
                                    <h4>Portal Modules</h4>
                                    <p>Enable or disable modules to show in customer portal</p>
                                </div>

                                <div className="ppModuleList">
                                    {settings?.map((item) => {
                                        const meta = moduleMeta[item.label] || { icon: "⚙️", color: "#6c5ce7", bg: "#f4f2ff", desc: "" };
                                        return (
                                            <div className="ppModuleRow" key={item.label}>
                                                <span className="ppDragHandle">⋮⋮</span>
                                                <div className="ppModuleIcon" style={{ background: meta.bg, color: meta.color }}>
                                                    {meta.icon}
                                                </div>
                                                <div className="ppModuleText">
                                                    <strong>{item.label}</strong>
                                                    <span>{meta.desc}</span>
                                                </div>
                                                <input
                                                    className="form-check-input ppModuleToggle"
                                                    type="checkbox"
                                                    checked={item.enabled}
                                                    onChange={() => handleToggle(item.label)}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>

                                <div className="ppSettingsFooter">
                                    <span className="ppDirtyBadge">Unsaved changes</span>
                                    <button className="ppSaveBtn" onClick={handleSave}>
                                        Save Changes
                                    </button>
                                </div>
                            </div>

                            {/* RIGHT: Live preview matching actual portal design */}
                            <div className="ppPreviewCard">
                                <div className="ppPreviewHeader">
                                    <h5>Portal Preview</h5>
                                </div>

                                <div className="ppPortalScroll">
                                    <div className="ppPortalTopBar">
                                        <div className="ppPortalLogo">PB</div>
                                        <div className="ppPortalUser">
                                            <span>{decrypt(localStorage.getItem("name"))}</span>
                                            <div className="ppPortalIconCircle">🛒</div>
                                            <div className="ppPortalAvatarCircle">👤</div>
                                        </div>
                                    </div>

                                    <div className="ppLoyaltyBanner">
                                        <span className="ppLoyaltyTag">LOYALTY REWARDS</span>
                                        <h3>Earn Points</h3>
                                        <h3 className="ppOrange">Every Purchase</h3>
                                        <p>Shop &amp; scan to collect reward points</p>
                                        <div className="ppLoyaltyIconWrap">🛍️</div>
                                    </div>

                                    <div className="ppLoyaltyDots">
                                        <span></span><span></span><span></span>
                                    </div>

                                    <div className="ppPtsStatsRow">
                                        <div className="ppPtsStatBox">
                                            <strong>110,050</strong>
                                            <small>Earned</small>
                                        </div>
                                        <div className="ppPtsStatBox ppActivePts">
                                            <strong>110,050</strong>
                                            <small>Available</small>
                                        </div>
                                        <div className="ppPtsStatBox">
                                            <strong>0</strong>
                                            <small>Redeemed</small>
                                        </div>
                                    </div>

                                    {goalEnabled && (
                                        <>
                                            <div className="ppSectionHeadRow">
                                                <h6>My Goal</h6>
                                                <a href="#">Change</a>
                                            </div>
                                            <div className="ppGoalCard">
                                                <div className="ppGoalTopRow">
                                                    <span className="ppActiveGoalTag">🎯 Active Goal</span>
                                                    <div className="ppGoalRing">
                                                        <strong>100%</strong>
                                                        <small>done</small>
                                                    </div>
                                                </div>
                                                <h4>Trip to Travel kenya</h4>
                                                <div className="ppGoalMeta">📅 3 Months &nbsp; Ends 21 Oct 2026</div>
                                                <div className="ppGoalBar">
                                                    <div className="ppGoalBarFill" style={{ width: "100%" }}></div>
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {leaderboardEnabled && (
                                        <>
                                            <div className="ppSectionHeadRow">
                                                <h6>Leaderboard</h6>
                                                <a href="#">View all</a>
                                            </div>
                                            <div className="ppLeaderboardCard">
                                                <div className="ppLeaderboardTopRow">
                                                    <span className="ppLeaderboardTag">🏆 Top Performers</span>
                                                </div>
                                                <div className="ppLeaderboardList">
                                                    {leaderboardPreviewData.map((row) => (
                                                        <div
                                                            className={`ppLeaderboardRow${row.isYou ? " ppIsYou" : ""}`}
                                                            key={row.rank}
                                                        >
                                                            <div className="ppLeaderboardRank">{row.rank}</div>
                                                            <div className="ppLeaderboardAvatar">
                                                                {row.name.charAt(0)}
                                                            </div>
                                                            <div className="ppLeaderboardName">{row.name}</div>
                                                            <div className="ppLeaderboardPts">{row.points} pts</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {visibleEarn.length > 0 && (
                                        <>
                                            <div className="ppSectionHeadRow">
                                                <h6>Earn Points</h6>
                                            </div>
                                            <div className="ppGrid2">
                                                {visibleEarn.map((label) => {
                                                    const m = earnPreviewMeta[label];
                                                    return (
                                                        <div className="ppEarnCard" key={label}>
                                                            <div className="ppEarnCardLeft">
                                                                <div className="ppEarnIcon" style={{ background: m.bg }}>{m.icon}</div>
                                                                <span>{m.label}</span>
                                                            </div>
                                                            <div className="ppEarnArrow">→</div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    )}

                                    {visibleRedeem.length > 0 && (
                                        <>
                                            <div className="ppSectionHeadRow">
                                                <h6>Redeem Points</h6>
                                            </div>
                                            <div className="ppGrid4">
                                                {visibleRedeem.map((label) => {
                                                    const m = redeemPreviewMeta[label];
                                                    return (
                                                        <div className="ppRedeemCard" key={label}>
                                                            <div className="ppRedeemIcon" style={{ background: m.bg }}>{m.icon}</div>
                                                            <span>{m.label}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    )}

                                    <div className="ppSectionHeadRow">
                                        <h6>Recent Transactions</h6>
                                        <a href="#">View all</a>
                                    </div>
                                    <div className="ppTxRow">
                                        <div className="ppTxLeft">
                                            <div className="ppTxIcon">+</div>
                                            <div>
                                                <strong>Scratch Card</strong>
                                                <small>16 Jul 2026</small>
                                            </div>
                                        </div>
                                        <div className="ppTxRight">+10</div>
                                    </div>
                                    <div className="ppTxRow">
                                        <div className="ppTxLeft">
                                            <div className="ppTxIcon">+</div>
                                            <div>
                                                <strong>Points Earned</strong>
                                                <small>16 Jul 2026</small>
                                            </div>
                                        </div>
                                        <div className="ppTxRight">+50</div>
                                    </div>

                                    {visibleEarn.length === 0 && visibleRedeem.length === 0 && !goalEnabled && !leaderboardEnabled && (
                                        <div className="ppEmptyPreviewNote">Enable modules on the left to see them appear here</div>
                                    )}
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </>
    );
};

export default PayByPoint;